import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaSave, FaTimes, FaUpload } from 'react-icons/fa';
import Select from 'react-select';
import useFetch from '../constants/useFetch';
import ButtonSpinner from '../components/ButtonSpinner';
import {
  API_BASE_URL,
  CLOUDINARY_CLOUD_NAME,
  CLOUDINARY_UPLOAD_PRESET,
} from '../constants';

const GENDER_OPTIONS = [
  { value: 'Male', label: 'Male' },
  { value: 'Female', label: 'Female' },
];

const BLOOD_GROUPS = [
  'A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-',
].map(v => ({ value: v, label: v }));

const NIGERIAN_STATES = [
  'Abia','Adamawa','Akwa Ibom','Anambra','Bauchi','Bayelsa','Benue','Borno',
  'Cross River','Delta','Ebonyi','Edo','Ekiti','Enugu','Gombe','Imo','Jigawa',
  'Kaduna','Kano','Katsina','Kebbi','Kogi','Kwara','Lagos','Nasarawa','Niger',
  'Ogun','Ondo','Osun','Oyo','Plateau','Rivers','Sokoto','Taraba','Yobe','Zamfara','FCT'
].map(v => ({ value: v, label: v }));

const CLASS_CATEGORY = {
  JUNIOR: 'junior',
  SENIOR: 'senior',
};

const AddStudent = () => {
  const navigate = useNavigate();

  // Pull everything you need for options (classes + subjects)
  const { data: dashboardData, loading, error: fetchError } = useFetch(
    `${API_BASE_URL}/api/web/admin/all-data`,
    'allData'
  );

  const classrooms = dashboardData?.classrooms || [];
  const juniorSubjectsRaw = dashboardData?.juniorSubjects || [];
  const seniorSubjectsRaw = dashboardData?.seniorSubjects || [];
  // Optional: if your API exposes "both" subjects; otherwise fallback to baseline "subjects"
  const bothSubjectsRaw = dashboardData?.bothSubjects || dashboardData?.subjects || [];

  // ------------ local state ------------
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errMsg, setErrMsg] = useState('');
  const [classCategory, setClassCategory] = useState(CLASS_CATEGORY.JUNIOR); // default junior
  const abortRef = useRef(null);

  const [previewUrl, setPreviewUrl] = useState(null);
  const [localFile, setLocalFile] = useState(null); // raw file for upload

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    middleName: '',
    dateOfBirth: '',
    gender: '',
    bloodGroup: '',
    address: '',
    stateOfOrigin: '',
    lga: '',
    className: '', // string; backend maps class_name -> classroom_id
    parentName: '',
    parentPhone: '',
    studentPhone: '',
    emergencyEmail: '',
    emergencyPhone: '',
    allergies: '',
    religion: 'Christian',
    nationality: 'Nigerian',
    previousSchool: '',
    previousClass: '',
    admissionDate: '',
    specialNeeds: '',
    house: '',
    // profilePicture will be the Cloudinary URL (string)
    profilePicture: '',
    // optional
    subjectIds: [], // array of IDs expected by backend
  });

  // ------------ helpers ------------
  const normSubject = s => {
    // Try to normalize to a consistent shape from any of your lists.
    // Expect at least: id, subject_name (or name), maybe level/category
    const id = s.id ?? s.subject_id ?? s.value ?? s._id;
    const name = s.subject_name ?? s.name ?? s.label ?? String(id);
    // For grouping/UI only:
    const level =
      s.level || s.category || s.subject_level || (s.is_core ? 'both' : undefined);
    return { id, name, level };
  };

  // Dedup by id and map to react-select options
  const toOptions = (arr) => {
    const map = new Map();
    arr.forEach((s) => {
      const n = normSubject(s);
      if (n.id != null && !map.has(n.id)) {
        map.set(n.id, { value: n.id, label: n.name, _raw: n });
      }
    });
    return Array.from(map.values());
  };

  // Subjects pools (deduped)
  const juniorOptions = useMemo(() => toOptions(juniorSubjectsRaw), [juniorSubjectsRaw]);
  const seniorOptions = useMemo(() => toOptions(seniorSubjectsRaw), [seniorSubjectsRaw]);
  const bothOptions   = useMemo(() => toOptions(bothSubjectsRaw), [bothSubjectsRaw]);

  // Available subjects based on category (junior: both + junior, senior: both + senior)
  const subjectOptions = useMemo(() => {
    const pool = classCategory === CLASS_CATEGORY.JUNIOR
      ? [...bothOptions, ...juniorOptions]
      : [...bothOptions, ...seniorOptions];

    // Dedup after merge
    const byId = new Map();
    pool.forEach(o => { if (!byId.has(o.value)) byId.set(o.value, o); });
    return Array.from(byId.values());
  }, [classCategory, bothOptions, juniorOptions, seniorOptions]);

  // Group for nicer UI (optional)
  const groupedSubjectOptions = useMemo(() => {
    const allowedBoth = bothOptions.filter(o => subjectOptions.some(s => s.value === o.value));
    const allowedJunior = juniorOptions.filter(o => subjectOptions.some(s => s.value === o.value));
    const allowedSenior = seniorOptions.filter(o => subjectOptions.some(s => s.value === o.value));
    return [
      allowedBoth.length ? { label: 'Core (Both)', options: allowedBoth } : null,
      classCategory === CLASS_CATEGORY.JUNIOR && allowedJunior.length
        ? { label: 'Junior-only', options: allowedJunior }
        : null,
      classCategory === CLASS_CATEGORY.SENIOR && allowedSenior.length
        ? { label: 'Senior-only', options: allowedSenior }
        : null,
    ].filter(Boolean);
  }, [classCategory, subjectOptions, bothOptions, juniorOptions, seniorOptions]);

  // Filter classes by category: junior -> class_name startsWith JSS (case-insensitive)
  const classOptions = useMemo(() => {
    const isJunior = classCategory === CLASS_CATEGORY.JUNIOR;
    const filtered = classrooms.filter(c => {
      const name = (c.class_name || '').toString();
      if (isJunior) return /^jss/i.test(name);       // must start with JSS for junior
      return !/^jss/i.test(name);                    // anything that is NOT JSS for senior
    });
    return filtered.map(c => ({
      value: c.class_name,
      label: c.class_name,
      _raw: c,
    }));
  }, [classrooms, classCategory]);

  // If switching category, drop inappropriate selections:
  useEffect(() => {
    // 1) className must be valid under the new category
    if (formData.className && !classOptions.some(c => c.value === formData.className)) {
      setFormData(prev => ({ ...prev, className: '' }));
    }
    // 2) subjectIds must also be valid under the new category
    if (formData.subjectIds?.length) {
      const allowedIds = new Set(subjectOptions.map(o => o.value));
      const cleaned = formData.subjectIds.filter(id => allowedIds.has(id));
      if (cleaned.length !== formData.subjectIds.length) {
        setFormData(prev => ({ ...prev, subjectIds: cleaned }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classCategory, classOptions, subjectOptions]);

  // ------------ handlers ------------
  const onText = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const onSelectClass = (opt) => {
    setFormData(prev => ({ ...prev, className: opt?.value || '' }));
  };

  const onSelectSubjects = (opts) => {
    // dedup + store IDs only
    const ids = (opts || []).map(o => o.value);
    const unique = Array.from(new Set(ids));
    setFormData(prev => ({ ...prev, subjectIds: unique }));
  };

  const onPickFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Validate client-side (your backend also checks)
    const allowed = ['image/jpeg', 'image/png', 'image/gif'];
    if (!allowed.includes(file.type)) {
      setErrMsg('Invalid file type. Only JPEG, PNG, and GIF are allowed.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setErrMsg('File size exceeds 5MB limit.');
      return;
    }
    setErrMsg('');
    setLocalFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setPreviewUrl(reader.result);
    reader.readAsDataURL(file);
  };

  const doCloudinaryUpload = async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
    fd.append('folder', 'schportal/students');

    abortRef.current = new AbortController();
    const res = await fetch(
      `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`,
      { method: 'POST', body: fd, signal: abortRef.current.signal }
    );
    const json = await res.json();
    if (!res.ok || !json?.secure_url) {
      throw new Error(json?.error?.message || 'Cloudinary upload failed');
    }
    return json.secure_url;
  };

  const validate = () => {
    const required = [
      'firstName', 'lastName', 'dateOfBirth', 'gender', 'address',
      'stateOfOrigin', 'lga', 'className', 'parentName', 'parentPhone',
      'admissionDate',
    ];
    for (const k of required) {
      if (!String(formData[k] || '').trim()) {
        return `Please fill in the ${k} field.`;
      }
    }
    // quick phone sanity (not strict)
    const phoneish = ['parentPhone', 'studentPhone', 'emergencyPhone'];
    for (const p of phoneish) {
      const v = (formData[p] || '').trim();
      if (v && !/^[\d+\-\s()]{7,}$/.test(v)) {
        return `Invalid ${p} format.`;
      }
    }
    // email check if provided
    if (formData.emergencyEmail && !/^\S+@\S+\.\S+$/.test(formData.emergencyEmail)) {
      return 'Invalid emergencyEmail format.';
    }
    return '';
  };

//   console.log({subjectIds: formData.subjectIds});
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrMsg('');

    const v = validate();
    if (v) {
      setErrMsg(v);
      return;
    }

    setIsSubmitting(true);
    let cloudUrl = '';

    try {
      // 1) Upload to Cloudinary if we have a local file
    //   if (localFile) {
    //     cloudUrl = await doCloudinaryUpload(localFile);
    //   }
    //   console.log({cloudUrl});
      
      // 2) Build payload for backend
      const payload = new FormData();

      // Text fields → req.body
      const fields = [
        'firstName','lastName','middleName','dateOfBirth','gender','bloodGroup',
        'address','stateOfOrigin','lga','className','parentName','parentPhone',
        'studentPhone','emergencyEmail','emergencyPhone','allergies','religion',
        'nationality','previousSchool','previousClass','admissionDate',
        'specialNeeds','house'
      ];
      fields.forEach((k) => {
        const val = formData[k];
        if (val !== undefined && val !== null && String(val).trim() !== '') {
          payload.append(k, val);
        }
      });

      // profilePicture (string URL, optional)
    //   if (cloudUrl) {
    //     // send cloud URL as profilePicture (backend stores it in `profile_picture`)
    //     payload.append('profilePicture', cloudUrl);
    //   }

      // Also attach raw file (so backend can read from req.files?.profile_picture if needed)
      if (localFile) {
        payload.append('profile_picture', localFile, localFile.name);
      }

      // subjectIds (optional)
      if (formData.subjectIds?.length) {
        // send as JSON array of IDs
        payload.append('subjectIds', JSON.stringify(formData.subjectIds));
        // payload.append('subjectIds', formData.subjectIds);
      }

      const res = await fetch(`${API_BASE_URL}/api/web/admin/add-student`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authToken')}`,
        },
        body: payload,
      });
      const json = await res.json();
      if (!res.ok || !json?.success) {
        throw new Error(json?.message || 'Failed to add student');
      }

      alert('Student added successfully!');
    //   navigate('/students');
    } catch (err) {
      setErrMsg(err.message || 'Something went wrong');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ------------ UI ------------
  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl p-6 text-white">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <div className="space-y-2">
            <h1 className="text-2xl font-bold">Add New Student</h1>
            <p className="text-blue-100">Fill in the details to add a new student</p>
          </div>
          <Link
            to="/classes"
            className="flex items-center space-x-2 bg-white/20 hover:bg-white/30 text-white px-6 py-3 rounded-lg transition-colors"
          >
            <FaArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </Link>
        </div>
      </div>

      {/* Form */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-200 dark:border-gray-700">
        {fetchError && <div className="text-red-600 text-center mb-4">{fetchError}</div>}
        {errMsg && <div className="text-red-600 text-center mb-4">{errMsg}</div>}
        {loading && (
          <div className="text-gray-600 dark:text-gray-400 text-center mb-4">
            Loading classes & subjects...
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Profile picture */}
          <div className="mb-8 flex flex-col items-center">
            <div className="relative w-32 h-32 mb-4">
              <div className="w-full h-full rounded-full overflow-hidden bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                {previewUrl ? (
                  <img src={previewUrl} alt="Preview" className="w-full h-full object-cover" />
                ) : (
                  <FaUpload className="text-gray-400 text-3xl" />
                )}
              </div>
              <label
                htmlFor="profilePic"
                className="absolute bottom-0 right-0 bg-blue-600 text-white p-2 rounded-full cursor-pointer hover:bg-blue-700 transition-colors"
              >
                <FaUpload className="text-sm" />
              </label>
              <input
                id="profilePic"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={onPickFile}
              />
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Upload profile picture (max 5MB; JPG/PNG/GIF)</p>
          </div>

          {/* Category toggle + Class */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="form-group md:col-span-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Class Category
              </label>
              <div className="flex gap-2">
                {[CLASS_CATEGORY.JUNIOR, CLASS_CATEGORY.SENIOR].map(cat => (
                  <button
                    type="button"
                    key={cat}
                    onClick={() => setClassCategory(cat)}
                    className={`px-4 py-2 rounded-lg border ${
                      classCategory === cat
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-transparent text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600'
                    }`}
                  >
                    {cat === CLASS_CATEGORY.JUNIOR ? 'Junior' : 'Senior'}
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Junior classes are restricted to names starting with <strong>JSS</strong>.
              </p>
            </div>

            <div className="form-group md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Class *
              </label>
              <Select
                options={classOptions}
                value={classOptions.find(o => o.value === formData.className) || null}
                onChange={onSelectClass}
                placeholder="Select a class"
                classNamePrefix="select"
                isDisabled={loading}
                required
              />
            </div>
          </div>

          {/* Basic info */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mt-6">
            <div className="form-group">
              <label className="block text-sm font-medium mb-2">First Name *</label>
              <input name="firstName" value={formData.firstName} onChange={onText}
                className="w-full px-4 py-2 border rounded-md dark:bg-gray-700 dark:text-white" required />
            </div>
            <div className="form-group">
              <label className="block text-sm font-medium mb-2">Middle Name</label>
              <input name="middleName" value={formData.middleName} onChange={onText}
                className="w-full px-4 py-2 border rounded-md dark:bg-gray-700 dark:text-white" />
            </div>
            <div className="form-group">
              <label className="block text-sm font-medium mb-2">Last Name *</label>
              <input name="lastName" value={formData.lastName} onChange={onText}
                className="w-full px-4 py-2 border rounded-md dark:bg-gray-700 dark:text-white" required />
            </div>
            <div className="form-group">
              <label className="block text-sm font-medium mb-2">Date of Birth *</label>
              <input type="date" name="dateOfBirth" value={formData.dateOfBirth} onChange={onText}
                className="w-full px-4 py-2 border rounded-md dark:bg-gray-700 dark:text-white" required />
            </div>

            <div className="form-group">
              <label className="block text-sm font-medium mb-2">Gender *</label>
              <Select
                options={GENDER_OPTIONS}
                value={GENDER_OPTIONS.find(o => o.value === formData.gender) || null}
                onChange={(o) => setFormData(p => ({ ...p, gender: o?.value || '' }))}
                placeholder="Select gender"
                classNamePrefix="select"
                isDisabled={loading}
                required
              />
            </div>

            <div className="form-group">
              <label className="block text-sm font-medium mb-2">Blood Group</label>
              <Select
                options={BLOOD_GROUPS}
                value={BLOOD_GROUPS.find(o => o.value === formData.bloodGroup) || null}
                onChange={(o) => setFormData(p => ({ ...p, bloodGroup: o?.value || '' }))}
                placeholder="Select blood group"
                classNamePrefix="select"
                isDisabled={loading}
              />
            </div>

            <div className="form-group">
              <label className="block text-sm font-medium mb-2">State of Origin *</label>
              <Select
                options={NIGERIAN_STATES}
                value={NIGERIAN_STATES.find(o => o.value === formData.stateOfOrigin) || null}
                onChange={(o) => setFormData(p => ({ ...p, stateOfOrigin: o?.value || '' }))}
                placeholder="Select state"
                classNamePrefix="select"
                isDisabled={loading}
                required
              />
            </div>

            <div className="form-group">
              <label className="block text-sm font-medium mb-2">LGA *</label>
              <input name="lga" value={formData.lga} onChange={onText}
                className="w-full px-4 py-2 border rounded-md dark:bg-gray-700 dark:text-white" required />
            </div>

            <div className="form-group md:col-span-2">
              <label className="block text-sm font-medium mb-2">Address *</label>
              <input name="address" value={formData.address} onChange={onText}
                className="w-full px-4 py-2 border rounded-md dark:bg-gray-700 dark:text-white" required />
            </div>

            <div className="form-group">
              <label className="block text-sm font-medium mb-2">Parent/Guardian Name *</label>
              <input name="parentName" value={formData.parentName} onChange={onText}
                className="w-full px-4 py-2 border rounded-md dark:bg-gray-700 dark:text-white" required />
            </div>

            <div className="form-group">
              <label className="block text-sm font-medium mb-2">Parent/Guardian Phone *</label>
              <input name="parentPhone" value={formData.parentPhone} onChange={onText}
                className="w-full px-4 py-2 border rounded-md dark:bg-gray-700 dark:text-white" required />
            </div>

            <div className="form-group">
              <label className="block text-sm font-medium mb-2">Student Phone</label>
              <input name="studentPhone" value={formData.studentPhone} onChange={onText}
                className="w-full px-4 py-2 border rounded-md dark:bg-gray-700 dark:text-white" />
            </div>

            <div className="form-group">
              <label className="block text-sm font-medium mb-2">Emergency Email</label>
              <input type="email" name="emergencyEmail" value={formData.emergencyEmail} onChange={onText}
                className="w-full px-4 py-2 border rounded-md dark:bg-gray-700 dark:text-white" />
            </div>

            <div className="form-group">
              <label className="block text-sm font-medium mb-2">Emergency Phone</label>
              <input name="emergencyPhone" value={formData.emergencyPhone} onChange={onText}
                className="w-full px-4 py-2 border rounded-md dark:bg-gray-700 dark:text-white" />
            </div>

            <div className="form-group md:col-span-2">
              <label className="block text-sm font-medium mb-2">Allergies</label>
              <input name="allergies" value={formData.allergies} onChange={onText}
                className="w-full px-4 py-2 border rounded-md dark:bg-gray-700 dark:text-white" />
            </div>

            <div className="form-group">
              <label className="block text-sm font-medium mb-2">Religion</label>
              <input name="religion" value={formData.religion} onChange={onText}
                className="w-full px-4 py-2 border rounded-md dark:bg-gray-700 dark:text-white" />
            </div>

            <div className="form-group">
              <label className="block text-sm font-medium mb-2">Nationality</label>
              <input name="nationality" value={formData.nationality} onChange={onText}
                className="w-full px-4 py-2 border rounded-md dark:bg-gray-700 dark:text-white" />
            </div>

            <div className="form-group">
              <label className="block text-sm font-medium mb-2">Previous School</label>
              <input name="previousSchool" value={formData.previousSchool} onChange={onText}
                className="w-full px-4 py-2 border rounded-md dark:bg-gray-700 dark:text-white" />
            </div>

            <div className="form-group">
              <label className="block text-sm font-medium mb-2">Previous Class</label>
              <input name="previousClass" value={formData.previousClass} onChange={onText}
                className="w-full px-4 py-2 border rounded-md dark:bg-gray-700 dark:text-white" />
            </div>

            <div className="form-group">
              <label className="block text-sm font-medium mb-2">Admission Date *</label>
              <input type="date" name="admissionDate" value={formData.admissionDate} onChange={onText}
                className="w-full px-4 py-2 border rounded-md dark:bg-gray-700 dark:text-white" required />
            </div>

            <div className="form-group md:col-span-2">
              <label className="block text-sm font-medium mb-2">Special Needs</label>
              <input name="specialNeeds" value={formData.specialNeeds} onChange={onText}
                className="w-full px-4 py-2 border rounded-md dark:bg-gray-700 dark:text-white" />
            </div>

            <div className="form-group md:col-span-2">
              <label className="block text-sm font-medium mb-2">House</label>
              <input name="house" value={formData.house} onChange={onText}
                className="w-full px-4 py-2 border rounded-md dark:bg-gray-700 dark:text-white" />
            </div>
          </div>

          {/* Subjects (optional, depends on category) */}
          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Subjects (optional)
            </label>
            <Select
              isMulti
              options={groupedSubjectOptions}
              // map formData.subjectIds -> options
              value={subjectOptions.filter(o => formData.subjectIds.includes(o.value))}
              onChange={onSelectSubjects}
              classNamePrefix="select"
              placeholder={
                classCategory === CLASS_CATEGORY.JUNIOR
                  ? 'Select from Core + Junior subjects'
                  : 'Select from Core + Senior subjects'
              }
              isDisabled={loading}
              closeMenuOnSelect={false}
            />
            <p className="text-xs text-gray-500 mt-2">
              We’ll store <strong>subjectIds</strong> only. Options auto-filter by class category and dedupe across lists.
            </p>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-4 mt-8">
            <Link
              to="/students"
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <FaTimes />
              Cancel
            </Link>
            <button
              disabled={isSubmitting || loading}
              type="submit"
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {isSubmitting ? (
                <>
                  <ButtonSpinner size="small" color="white" />
                  Creating...
                </>
              ) : (
                <>
                  <FaSave />
                  Create Student
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddStudent;
